import { useEffect, useState, useCallback } from 'react';
import { api } from '@/contexts/AuthContext';
import { Loader2, Bell, Filter } from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';

const STATUS_STYLES = {
  sent:         { bg: '#DCFCE7', fg: '#047857', label: 'Sent' },
  error:        { bg: '#FEF2F2', fg: '#B91C1C', label: 'Error' },
  no_tokens:    { bg: '#F3F4F6', fg: '#4B5563', label: 'No devices' },
  skipped_pref: { bg: '#FEF3C7', fg: '#B45309', label: 'Muted by user' },
  rate_limited: { bg: '#EDE9FE', fg: '#6D28D9', label: 'Rate-limited' },
};

const EVENT_LABELS = {
  match: 'Match', message: 'Message', like: 'Like', intro: 'Intro',
};

function relTime(iso) {
  if (!iso) return '';
  const sec = Math.floor((new Date() - new Date(iso)) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

function StatusChip({ status }) {
  const cfg = STATUS_STYLES[status] || { bg: '#F3F4F6', fg: '#374151', label: status };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: cfg.bg, color: cfg.fg }}
      data-testid={`push-status-${status}`}
    >
      {cfg.label}
    </span>
  );
}

export default function AdminPushLog() {
  const [rows, setRows] = useState([]);
  const [counters, setCounters] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ channel: '', event_type: '', status: '', hours: 72 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filters.channel) q.set('channel', filters.channel);
      if (filters.event_type) q.set('event_type', filters.event_type);
      if (filters.status) q.set('status', filters.status);
      if (filters.hours) q.set('hours', String(filters.hours));
      q.set('limit', '100');
      const r = await api.get(`/admin/push-log?${q.toString()}`);
      setRows(r.data.rows || []);
      setCounters(r.data.counters || {});
      setTotal(r.data.total || 0);
    } catch (e) {
      // Silent — admin page, no toast to avoid noise on 403 during dev.
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-white" data-testid="admin-push-log-page">
      <div className="px-5 pt-6 pb-3 flex items-center gap-3">
        <BackNavButton to="/admin" />
        <div className="flex items-center gap-2">
          <Bell size={20} className="text-zinc-700" />
          <h1 className="text-xl font-bold">Push send log</h1>
        </div>
      </div>

      <div className="px-5">
        {/* Counter tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5" data-testid="push-counters">
          {[
            ['expo:sent', 'Sent', '#DCFCE7', '#047857'],
            ['expo:no_tokens', 'No devices', '#F3F4F6', '#4B5563'],
            ['expo:skipped_pref', 'Muted', '#FEF3C7', '#B45309'],
            ['expo:rate_limited', 'Rate-limited', '#EDE9FE', '#6D28D9'],
            ['expo:error', 'Errored', '#FEF2F2', '#B91C1C'],
          ].map(([k, lbl, bg, fg]) => (
            <div key={k} className="rounded-xl p-3" style={{ background: bg }}>
              <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: fg }}>{lbl}</div>
              <div className="text-2xl font-black" style={{ color: fg }}>{counters[k] || 0}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Filter size={14} className="text-zinc-500" />
          <select value={filters.event_type} data-testid="filter-event-type"
            onChange={e => setFilters(f => ({ ...f, event_type: e.target.value }))}
            className="text-[13px] border border-black/[0.08] rounded-lg px-2 py-1.5">
            <option value="">All events</option>
            <option value="match">Match</option>
            <option value="message">Message</option>
            <option value="like">Like</option>
            <option value="intro">Intro</option>
          </select>
          <select value={filters.status} data-testid="filter-status"
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            className="text-[13px] border border-black/[0.08] rounded-lg px-2 py-1.5">
            <option value="">All statuses</option>
            <option value="sent">Sent</option>
            <option value="error">Error</option>
            <option value="no_tokens">No devices</option>
            <option value="skipped_pref">Muted by user</option>
            <option value="rate_limited">Rate-limited</option>
          </select>
          <select value={filters.hours} data-testid="filter-hours"
            onChange={e => setFilters(f => ({ ...f, hours: Number(e.target.value) }))}
            className="text-[13px] border border-black/[0.08] rounded-lg px-2 py-1.5">
            <option value={24}>Last 24h</option>
            <option value={72}>Last 3 days</option>
            <option value={168}>Last 7 days</option>
            <option value={720}>Last 30 days</option>
          </select>
          <span className="text-[12px] text-zinc-500 ml-auto">
            {loading ? <Loader2 size={12} className="inline animate-spin" /> : `${total} rows`}
          </span>
        </div>

        {/* Table */}
        <div className="border border-black/[0.06] rounded-xl overflow-hidden">
          <div className="grid grid-cols-[80px_100px_1fr_180px_80px] text-[10px] uppercase tracking-wider font-semibold text-zinc-500 bg-zinc-50 px-3 py-2">
            <div>Event</div>
            <div>Status</div>
            <div>Title / body</div>
            <div>Recipient</div>
            <div className="text-right">Time</div>
          </div>
          {rows.map((r, i) => (
            <div key={i} data-testid={`push-log-row-${i}`}
              className="grid grid-cols-[80px_100px_1fr_180px_80px] text-[12.5px] px-3 py-2.5 border-t border-black/[0.04] hover:bg-zinc-50">
              <div className="font-semibold text-zinc-900">{EVENT_LABELS[r.event_type] || r.event_type}</div>
              <div><StatusChip status={r.status} /></div>
              <div className="text-zinc-700 min-w-0 pr-3">
                <div className="font-semibold truncate">{r.title}</div>
                <div className="text-zinc-500 truncate text-[11.5px]">{r.body_preview}</div>
              </div>
              <div className="text-zinc-700 truncate">
                <div className="font-medium">{r.recipient_name || '—'}</div>
                <div className="text-[10.5px] text-zinc-400 truncate">{r.recipient_id}</div>
              </div>
              <div className="text-right text-zinc-500">{relTime(r.created_at)}</div>
            </div>
          ))}
          {!loading && rows.length === 0 && (
            <div className="py-10 text-center text-zinc-400 text-[13px]">No push notifications in this window.</div>
          )}
        </div>
      </div>
    </div>
  );
}
